import { useBoundStore } from "./components/canvas/stores/useAppStore";
import { ModelItemNameEditJS, UserIdentification } from "target_wasm";
import type { CanvasFocusType } from "components/canvas/IFocus";
import type { CanvasEditType } from "components/canvas/stagedEdit/StagedEditType";
import {
  TaskType,
  ToWorkerMessage,
  type ExportClipboardResult,
} from "Worker/entities";
import { requestFromWorker } from "Worker/workerRequest";
import { showNotificationGlobal } from "./stores/useNotificationStore";
import {
  resolveDatasetMember,
  resolveDatasetMemberFailureAnonymous,
} from "components/canvas/utils/datasetImportBatch";
import { writeClipboard } from "./components/canvas/utils/clipboard";
import { formatBytes } from "components/canvas/detailsSide/utils/formatBytes";

export function putIntoClipboard(
  data: string,
  mycorrMeta: string | null | undefined,
  item_type: string,
  show_snackbar: boolean,
) {
  writeClipboard(data, mycorrMeta)
    .then(() => {
      if (show_snackbar) {
        showCopySuccessSnackbar(item_type);
      }
    })
    .catch(() => {
      showNotificationGlobal("Failed to copy to clipboard", "error");
    });
}

export function fetchViewForClipboard(
  requestBody: string,
  _isImported: boolean,
  colSettingsJson: string,
  columnNamesJson: string,
  categoryLabelsJson: string,
  mycorrMeta: string | null | undefined,
) {
  // Views whose rows aren't in the local store (imported and connector-backed
  // tables) stream their IPC from the server via the worker. The clipboard
  // write is issued here, still inside the copy keypress, and handed the
  // pending TSV — deferring it until the worker replies would land outside the
  // user gesture, which Safari rejects.
  const worker = useBoundStore.getState().worker;
  if (!worker) {
    showNotificationGlobal("Failed to copy — worker not ready", "error");
    return;
  }

  const tsv = requestFromWorker<ExportClipboardResult>(
    worker,
    TaskType.ExportClipboard,
    {
      requestBody,
      colSettingsJson,
      columnNamesJson,
      categoryLabelsJson,
    },
  ).then((result) => result.tsvString);

  writeClipboard(tsv, mycorrMeta)
    .then(() => showCopySuccessSnackbar("Table View"))
    .catch(() => {
      showNotificationGlobal("Failed to copy to clipboard", "error");
    });
}

///
/// Please always specify the arguments the function takes and map them in the Rust js_imports
/// This is much better than returning the callback and leaving it up to the caller in Rust
/// to "guess" the function arguments
/// 19 April 2024

export function setCanvasFocus(focus: CanvasFocusType) {
  const state = useBoundStore.getState();
  state.setCanvasFocus(focus);
}

export function useSetColumnSelection(columnIds: string[]) {
  const state = useBoundStore.getState();
  state.setCanvasColumnSelection(columnIds);
}

export function useClearColumnSelection() {
  const state = useBoundStore.getState();
  state.setCanvasColumnSelection(null);
}

export function invalidateModelStructure() {
  useBoundStore.getState().bumpModelStructureVersion();
}

export function invalidateDetail() {
  useBoundStore.getState().bumpDetailVersion();
}

export function invalidateTableHistory() {
  const state = useBoundStore.getState();
  state.invalidateTableHistory();
}

export function setCanvasInput(edit: {
  type: string;
  table_id?: string;
  column_id?: string;
  view_id?: string | null;
  item_id?: string;
  row_id?: number;
  initial_text?: string;
  edit_previous?: boolean;
  edit_entity?: string;
  column_data_type?: string;
  is_type_locked?: boolean;
  display_timezone?: string;
  decimal_places?: number;
  link_enabled?: boolean;
  is_computed?: boolean;
  column_name?: string;
  initial_term?: string;
  match_count?: number | null;
  scanning?: boolean;
}) {
  const ENTITY_MAP: Record<string, ModelItemNameEditJS> = {
    TableView: ModelItemNameEditJS.TableView,
    ModelItem: ModelItemNameEditJS.ModelItem,
    Table: ModelItemNameEditJS.Table,
  };
  const state = useBoundStore.getState();
  let canvasEdit: CanvasEditType;
  switch (edit.type) {
    case "ColumnName":
      if (!edit.view_id) {
        console.error("ColumnName edit overlay missing view_id");
        return;
      }
      canvasEdit = {
        type: "ColumnName",
        initial: edit.initial_text ?? "",
        table_id: edit.table_id!,
        column_id: edit.column_id!,
        fast_edit: edit.edit_previous ?? false,
        view_id: edit.view_id,
      };
      break;
    case "CellValue":
      canvasEdit = {
        type: "CellValue",
        initial: edit.initial_text ?? "",
        table_id: edit.table_id!,
        view_id: edit.view_id!,
        column_id: edit.column_id!,
        row_id: BigInt(edit.row_id!),
        fast_edit: edit.edit_previous ?? false,
        column_data_type: edit.column_data_type,
        is_type_locked: edit.is_type_locked,
        display_timezone: edit.display_timezone,
        decimal_places: edit.decimal_places,
        link_enabled: edit.link_enabled,
        is_computed: edit.is_computed,
      };
      break;
    case "ModelItemName":
      canvasEdit = {
        type: "ModelItemName",
        initial: edit.initial_text ?? "",
        item_id: edit.item_id!,
        fast_edit: edit.edit_previous ?? false,
        edit_entity: ENTITY_MAP[edit.edit_entity!],
      };
      break;
    case "ColumnSearch":
      canvasEdit = {
        type: "ColumnSearch",
        view_id: edit.view_id!,
        column_id: edit.column_id!,
        column_name: edit.column_name ?? "",
        match_count: edit.match_count ?? null,
        scanning: edit.scanning ?? false,
        initial: edit.initial_term ?? "",
        fast_edit: false,
      };
      break;
    case "ColumnFormula":
      canvasEdit = {
        type: "ColumnFormula",
        table_id: edit.table_id!,
        view_id: edit.view_id!,
        column_id: edit.column_id!,
        initial: "",
        fast_edit: false,
      };
      break;
    default:
      return;
  }
  state.setCanvasInput(canvasEdit);
}
export function useUpdateActiveUsers(): (users: unknown[]) => void {
  return (users: unknown[]) => {
    const state = useBoundStore.getState();

    try {
      if (!users || !Array.isArray(users)) {
        console.warn(
          "useUpdateActiveUsers received invalid users data:",
          users,
        );
        state.setActiveUsers([]);
        return;
      }

      const userIdentifications: UserIdentification[] = users
        .filter((user) => user != null)
        .map((user: unknown) => {
          return UserIdentification.from_js(user);
        });

      state.setActiveUsers(userIdentifications);
    } catch (error) {
      console.error("Failed to update active users:", error);
    }
  };
}

export function getUserNameByClientId(client_id: string): string {
  const state = useBoundStore.getState();
  const user = state.activeUsers.find((u) => u.user_id_js() === client_id);
  if (user) {
    return user.user_name_js();
  }
  return client_id.substring(0, 8);
}

export function getUserColor(client_id: string) {
  const state = useBoundStore.getState();
  const user = state.activeUsers.find((u) => u.user_id_js() === client_id);
  if (user) {
    return user.user_color_js();
  }
  return "var(--color-fg-muted)";
}
export function useUpdateTableDeletionState(
  table_id: string,
  hard_deletion: boolean,
  is_scheduled: boolean,
) {
  const state = useBoundStore.getState();
  state.setTableDeletionState(table_id, hard_deletion, is_scheduled);
}

export function setCanvasContentInfocus(isInFocus: boolean) {
  const state = useBoundStore.getState();
  return state.setCanvasContentInFocus(isInFocus);
}

/** Mirrors `client_core::graph::CanvasMode`. */
export type CanvasMode = "tabular" | "graph" | "rowGraph";

export function setCanvasMode(mode: CanvasMode) {
  useBoundStore.getState().setCanvasMode(mode);
}

export function setSelectedRowCount(count: number) {
  useBoundStore.getState().setSelectedRowCount(count);
}

export function setRowGraphSize(count: number) {
  useBoundStore.getState().setRowGraphSize(count);
}

export function onSnapshotLoaded() {
  // Model readiness is now driven by is_model_ready() in WASM.
  // This callback is kept as a no-op for the WASM FFI contract.
}

export function useSendClientDataRequest(
  request_id: string,
  serialized_message: string,
  table_id?: string,
) {
  const state = useBoundStore.getState();
  const workerMessage: ToWorkerMessage = {
    task: TaskType.ClientDataRequest,
    data: { request_id, request: serialized_message, table_id },
  };
  state.sendMessageToWorker(workerMessage);
}

function showCopySuccessSnackbar(item: string) {
  showNotificationGlobal(`${item} copied to clipboard`, "success");
}

export function showNotification(kind: {
  type: string;
  table_name?: string;
  source?: string;
  rows?: number;
  cols?: number;
  reason?: string;
  row_count?: number;
  bytes?: number;
  limit?: number;
  ended?: boolean;
  requested?: number;
  free?: number;
  pairs?: number;
  at_least?: boolean;
}) {
  switch (kind.type) {
    case "CastSuccess":
      showNotificationGlobal("Column casted successfully", "success");
      break;
    case "CopyDisabledSealedSpace":
      showNotificationGlobal(
        "Copying is disabled — this model is in a sealed space",
        "warning",
      );
      break;
    case "RowGraphAccessLost":
      showNotificationGlobal(
        kind.ended
          ? "Investigation closed — its tables are no longer accessible"
          : "Some rows were removed — their table is no longer accessible",
        "warning",
      );
      break;
    case "RowGraphFull":
      showNotificationGlobal(
        kind.free
          ? `Row graph is full — room for ${kind.free} more, not ${kind.requested}`
          : "Row graph is full — clear it or remove rows to add more",
        "warning",
      );
      break;
    case "RowGraphCollapseHeld":
      showNotificationGlobal(
        (kind.rows ?? 0) === 1
          ? "That row was expanded further — collapse its own links first."
          : `Those ${kind.rows} rows were expanded further — collapse their own links first.`,
        "warning",
      );
      break;
    case "RowGraphLinkTooBig": {
      const n = `${kind.at_least ? "over " : ""}${(kind.rows ?? 0).toLocaleString()}`;
      showNotificationGlobal(
        `That link brings in ${n} rows — the graph has room for ${(kind.free ?? 0).toLocaleString()}. Nothing was added.`,
        "warning",
      );
      break;
    }
    case "RowGraphEdgesIncomplete":
      showNotificationGlobal(
        `Too many linked tables to check at once — connections between ${kind.pairs} table pairs are not shown`,
        "warning",
      );
      break;
    case "CastError":
      showNotificationGlobal("Failed to cast column", "error");
      break;
    case "PasteSuccess":
      showNotificationGlobal(
        `Table "${kind.table_name}" created successfully`,
        "success",
      );
      break;
    case "TableImportPending":
      // Dataset-drop members roll into the batch toast instead of a
      // per-table one (single-slot store — they'd overwrite each other).
      // `?? ""` mirrors how the batch registers unnamed members.
      if (resolveDatasetMember(kind.table_name ?? "", "deferred")) break;
      showNotificationGlobal(
        `Table "${kind.table_name}" import pending approval`,
      );
      break;
    case "TableImportWaitingForRelease":
      if (resolveDatasetMember(kind.table_name ?? "", "deferred")) break;
      showNotificationGlobal(
        `"${kind.table_name}" import pending — waiting for release`,
      );
      break;
    case "TableUploadSuccess": {
      const actionLabel = kind.source === "FileUpload" ? "created" : "imported";
      showNotificationGlobal(
        `Table "${kind.table_name}" ${actionLabel} successfully`,
        "success",
      );
      break;
    }
    case "TableImportSuccess":
      if (resolveDatasetMember(kind.table_name ?? "", "success")) break;
      showNotificationGlobal(
        `Table "${kind.table_name}" imported successfully`,
        "success",
      );
      break;
    case "TableImportFailure":
      // The failure ACK carries no table identity — attribute it to the
      // active dataset batch, if any.
      if (resolveDatasetMemberFailureAnonymous()) break;
      showNotificationGlobal(`Import failed — ${kind.reason}`, "error");
      break;
    case "TableDetachSuccess":
      showNotificationGlobal(
        `Table "${kind.table_name}" detached successfully`,
        "success",
      );
      break;
    case "TableDetachFailure":
      showNotificationGlobal(
        kind.reason
          ? `Detach failed: ${kind.reason}`
          : "Detach failed — please try again",
        "error",
      );
      break;
    case "OwnershipTransferSuccess":
      showNotificationGlobal(
        `Table "${kind.table_name}" ownership transferred successfully`,
        "success",
      );
      break;
    case "InvalidFilterColumnRemoval":
      showNotificationGlobal(
        "Can't remove a column used in a filter or sort.",
        "error",
      );
      break;
    case "RemovalFailed":
      showNotificationGlobal(
        "Failed to remove: recently updated by another user",
        "error",
      );
      break;
    case "ModelStateResyncing":
      // The one warning that is still running: `progress: 0` is how a caller
      // asks for the spinner, and it holds until Resynced or Stuck replaces it.
      showNotificationGlobal(
        "Out of sync — reloading from server…",
        "warning",
        undefined,
        0,
      );
      break;
    case "ModelStateResynced":
      showNotificationGlobal("Back in sync with the server", "success");
      break;
    case "ModelStateStuck":
      showNotificationGlobal(
        "Couldn't get back in sync. Please reload the page.",
        "error",
      );
      break;
    case "CheckpointDeletedError":
      showNotificationGlobal("Failed to delete checkpoint", "error");
      break;
    case "TableFastForwardSuccess":
      showNotificationGlobal("Updated to the latest version", "success");
      break;
    case "TableFastForwardError":
      showNotificationGlobal(
        "Failed to update to the latest version.",
        "error",
      );
      break;
    case "CheckpointRestoreSuccess":
      showNotificationGlobal("Checkpoint restored successfully", "success");
      break;
    case "CheckpointRestoreError":
      showNotificationGlobal("Failed to restore checkpoint", "error");
      break;
    case "CopyTooLarge":
      showNotificationGlobal(
        `Selection too large to copy (${kind.rows?.toLocaleString()}×${kind.cols?.toLocaleString()} cells). Select fewer rows or columns.`,
        "error",
      );
      break;
    case "CellValueTooLarge":
      showNotificationGlobal(
        `Value too large for one cell (${formatBytes(kind.bytes ?? 0)}, limit ${formatBytes(kind.limit ?? 0)}).`,
        "error",
      );
      break;
    case "CopyFailed":
      showNotificationGlobal("Failed to copy to clipboard", "error");
      break;
    case "CopyLoadingData":
      showNotificationGlobal("Loading data for copy...");
      break;
    case "DiffAlreadyExists":
      showNotificationGlobal("Diff already exists", "default");
      break;
    case "FilterApplied":
      showNotificationGlobal("Filter applied", "success");
      break;
    case "FilterDeleted":
      showNotificationGlobal("Filter deleted", "success");
      break;
    case "FilterFailed":
      showNotificationGlobal(
        "Failed to apply filter. Please try again.",
        "error",
      );
      break;
    case "CannotCreateView":
      showNotificationGlobal(
        kind.reason
          ? `Cannot create view: ${kind.reason}`
          : "Cannot create view on this table",
        "error",
      );
      break;
    case "ExtractUniqueSuccess": {
      const count = kind.row_count ?? 0;
      const plural = count === 1 ? "value" : "values";
      showNotificationGlobal(
        `Created table "${kind.table_name}" with ${count} unique ${plural}`,
        "success",
      );
      break;
    }
    case "ExtractUniqueError":
      showNotificationGlobal(
        kind.reason || "Failed to extract unique values",
        "error",
      );
      break;
    case "TableHardDeleteSuccess":
      showNotificationGlobal(
        `"${kind.table_name}" permanently deleted`,
        "success",
      );
      break;
    case "TableHardDeleteFailure":
      showNotificationGlobal(
        `Couldn't permanently delete the table — ${kind.reason}.`,
        "error",
      );
      break;
  }
}

export function showConflictDialog(kind: {
  type: string;
  table_name?: string;
  column_name?: string;
}) {
  switch (kind.type) {
    case "Column":
      useBoundStore.getState().setConflictDialog({
        title: "Column Conflict",
        message: `Column "${kind.column_name}" in "${kind.table_name}" was modified by another user. Force remove?`,
      });
      break;
    case "Table":
      useBoundStore.getState().setConflictDialog({
        title: "Table Conflict",
        message: `Table "${kind.table_name}" was modified by another user. Force remove?`,
      });
      break;
  }
}

/*
Presence callbacks
*/

export function onFollowStateChanged(followingUserId: string): void {
  useBoundStore
    .getState()
    .setFollowingUserId(followingUserId === "" ? null : followingUserId);
}

export function onSessionAway(_clientId: string): void {}

export function onSessionActive(_clientId: string): void {}

export function onUserInfoUpdated(userData: unknown): void {
  try {
    const updatedUser = UserIdentification.from_js(userData);
    const state = useBoundStore.getState();
    const updatedUsers = state.activeUsers.map((u) =>
      u.user_id_js() === updatedUser.user_id_js() ? updatedUser : u,
    );
    state.setActiveUsers(updatedUsers);
  } catch (error) {
    console.error("Failed to handle user info update:", error);
  }
}

/// Module-level ref to the cursor animation start callback.
/// Set by CursorCanvasContainer; called by WASM when a cursor update arrives.
let _onCursorUpdateCallback: (() => void) | null = null;

export function registerOnCursorUpdate(fn: (() => void) | null): void {
  _onCursorUpdateCallback = fn;
}

export function onCursorUpdate(): void {
  _onCursorUpdateCallback?.();
}

/*
Viewport Persistence
*/

export function saveViewport(
  model_id: string,
  x: number,
  y: number,
  zoom: number,
) {
  try {
    localStorage.setItem(
      `viewport-${model_id}`,
      JSON.stringify({ x, y, zoom }),
    );
  } catch {
    /* full storage or private browsing */
  }
}

export function getSavedViewport(
  model_id: string,
): { x: number; y: number; zoom: number } | null {
  try {
    const raw = localStorage.getItem(`viewport-${model_id}`);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return typeof p.x === "number" &&
      typeof p.y === "number" &&
      typeof p.zoom === "number"
      ? p
      : null;
  } catch {
    return null;
  }
}

/*
Parent Model
*/

export function onParentModelUpdate(
  tableId: string,
  parentModelId: string,
  parentModelName: string,
  parentModelColor?: string,
  parentModelIconIndex?: number,
): void {
  useBoundStore
    .getState()
    .setParentModel(
      tableId,
      parentModelId,
      parentModelName,
      parentModelColor,
      parentModelIconIndex,
    );
}

/*
Table Permissions
*/

export function onTablePermissionsUpdate(): void {
  useBoundStore.getState().bumpPermissionsVersion();
}

export function onModelPermissionsUpdate(): void {
  useBoundStore.getState().bumpPermissionsVersion();
}

/*
UI panels — called from WASM via UiCallbacks
*/

export function openSearch(): void {
  window.dispatchEvent(new CustomEvent("request-search"));
}

export interface OverflowCategoryChip {
  label: string;
  color: string | null;
}

export interface OverflowIconInfo {
  text: string;
  truncated: boolean;
  shown_bytes: number;
  full_bytes: number;
  view_id: string;
  row_id: number;
  col_id: string;
  col_name: string;
  icon_screen_x: number;
  icon_screen_y: number;
  cell_screen_x: number;
  cell_screen_y: number;
  cell_width: number;
  categories?: OverflowCategoryChip[];
  is_link: boolean;
}

export function setOverflowIcon(icon: OverflowIconInfo | null): void {
  useBoundStore.getState().setOverflowIcon(icon);
}

export interface FormulaPeekInfo {
  table_id: string;
  view_id: string;
  column_id: string;
  column_name: string;
  header_left: number;
  header_top: number;
  header_bottom: number;
  header_width: number;
  can_edit: boolean;
  runtime_error: boolean;
}

export function setFormulaPeek(peek: FormulaPeekInfo | null): void {
  useBoundStore.getState().setFormulaPeek(peek);
}

/**
 * Graph node chips. Absolute screen px, pushed at discrete changes only — a pan
 * or zoom dismisses them rather than moving them, so no frame loop is involved.
 */
export interface GraphImportChipInfo {
  table_id: string;
  screen_x: number;
  screen_y: number;
}

export function setGraphImportChip(chip: GraphImportChipInfo | null): void {
  useBoundStore.getState().setGraphImportChip(chip);
}

export interface GraphExpandChipInfo {
  collapse: boolean;
  count: number;
  screen_x: number;
  screen_y: number;
}

export function setGraphExpandChip(chip: GraphExpandChipInfo | null): void {
  useBoundStore.getState().setGraphExpandChip(chip);
}

export interface RowGraphOverlayLink {
  table_id: string;
  name: string;
  expandable: boolean;
  expanded: boolean;
  other_model: boolean;
}

export interface RowGraphOverlayInfo {
  focused_node: string;
  focused_label: string;
  focused_table_name: string;
  links: RowGraphOverlayLink[];
}

export function setRowGraphOverlay(overlay: RowGraphOverlayInfo | null): void {
  useBoundStore.getState().setRowGraphOverlay(overlay);
}

// WASM asks for a closure round trip (build request → POST → feed back).
// Dynamic import: exports.tsx loads before the canvas tree, and the fetch
// helper lives beside the expand it mirrors.
export function requestRowGraphClose(): void {
  void import("./components/canvas/RowGraphOverlay").then((m) =>
    m.closeRowGraph(),
  );
}

/** One expansion to run again — the row it ran from, and the table it reached. */
export interface RowGraphReexpand {
  source_node: string;
  table_id: string;
}

// WASM asks for expansions to be run again after a data change.
export function requestRowGraphReexpand(expansions: RowGraphReexpand[]): void {
  if (expansions.length === 0) return;
  void import("./components/canvas/RowGraphOverlay").then((m) =>
    m.reexpandRowGraph(expansions),
  );
}

export interface LinkIconInfo {
  text: string;
  icon_screen_x: number;
  icon_screen_y: number;
  before_overflow: boolean;
}

export function setLinkIcon(icon: LinkIconInfo | null): void {
  useBoundStore.getState().setLinkIcon(icon);
}

// ─── Cross-model TableLink HTTP helpers ─────────────────────────────────
//
// Mutations and reads against /server/action/links go through these
// JS-imported helpers (declared in `wasm_client/src/js_imports.rs`). The
// WASM side is fire-and-forget; on response, we call back into the
// `clientApp` via the matching `on_link_*` methods so the cache stays in
// sync. Failures are reported via the notification store.
//
// The cache is per-table TTL+SWR (10 min); peer mutations show up either
// after the next refetch or immediately when the user clicks the
// "Refresh links" affordance in Phase 6 UI.

const LINKS_ENDPOINT = "/server/action/links";

function reportLinksError(action: string, err: unknown): void {
  console.error(`linksApi ${action} failed:`, err);
  const reason = err instanceof LinksApiError ? err.reason : null;
  showNotificationGlobal(
    reason ? `Link ${action} failed: ${reason}` : `Link ${action} failed`,
    "error",
  );
}

/// Carries the structured 403 body (`{ error, reason }`) the link
/// endpoints return from `require_link_authority`, so a refusal that
/// slips past client-side gating explains itself in the toast.
class LinksApiError extends Error {
  reason: string | null;
  constructor(status: number, reason: string | null) {
    super(`HTTP ${status}`);
    this.reason = reason;
  }
}

async function throwLinksApiError(res: Response): Promise<never> {
  let reason: string | null = null;
  try {
    const body = (await res.json()) as { reason?: string };
    reason = typeof body.reason === "string" ? body.reason : null;
  } catch {
    /* non-JSON error body — fall back to the bare status */
  }
  throw new LinksApiError(res.status, reason);
}

// The link cache lives in WASM; React subscribes to `linksVersion` so any
// component that reads `get_all_table_links_grouped()` re-renders after a
// successful HTTP round-trip. Bump after every `on_link_*` callback.
function bumpLinksVersion(): void {
  useBoundStore.getState().bumpLinksVersion();
}

export function linksCreate(requestBody: string): void {
  fetch(LINKS_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: requestBody,
    credentials: "include",
  })
    .then(async (res) => {
      if (!res.ok) {
        await throwLinksApiError(res);
      }
      const linkJson = await res.text();
      const clientApp = useBoundStore.getState().clientApp;
      clientApp?.on_link_created(linkJson);
      bumpLinksVersion();
    })
    .catch((err) => reportLinksError("create", err));
}

export function linksDelete(linkId: string): void {
  fetch(`${LINKS_ENDPOINT}/${encodeURIComponent(linkId)}`, {
    method: "DELETE",
    credentials: "include",
  })
    .then(async (res) => {
      // 204 (success) and 404 (already gone — idempotent) are both fine;
      // either way we evict the cached row locally.
      if (!res.ok && res.status !== 404) {
        await throwLinksApiError(res);
      }
      const clientApp = useBoundStore.getState().clientApp;
      clientApp?.on_link_deleted(linkId);
      bumpLinksVersion();
    })
    .catch((err) => reportLinksError("delete", err));
}

export function linksFetchForTable(tableId: string): void {
  const url = `${LINKS_ENDPOINT}?for_table=${encodeURIComponent(tableId)}`;
  const setStatus = (s: "loaded" | "denied" | "error") =>
    useBoundStore.getState().setLinksFetchStatus(tableId, s);
  fetch(url, { credentials: "include" })
    .then(async (res) => {
      if (res.status === 403) {
        // 403 ≠ "no links": render a denied-access message.
        setStatus("denied");
        bumpLinksVersion();
        console.warn(`linksFetchForTable: 403 for ${tableId}`);
        return;
      }
      if (res.status === 404) {
        // 404 = table gone; treat as "no links" rather than an error.
        setStatus("loaded");
        bumpLinksVersion();
        console.warn(`linksFetchForTable: 404 for ${tableId}`);
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const linksJson = await res.text();
      const clientApp = useBoundStore.getState().clientApp;
      clientApp?.on_link_list_received_for_table(tableId, linksJson);
      setStatus("loaded");
      bumpLinksVersion();
    })
    .catch((err) => {
      // Distinct from "denied" so the section can offer a retry.
      setStatus("error");
      bumpLinksVersion();
      reportLinksError("fetch (table)", err);
    });
}

/// Batched form of `linksFetchForTable` — one request for up to 100 tables,
/// dispatched whenever a whole model's links are warmed (model load, graph
/// entry, fresh import). `tableIdsCsv` is comma-separated and pre-chunked by
/// WASM; a larger batch is a 400.
export function linksFetchForTables(tableIdsCsv: string): void {
  const tableIds = tableIdsCsv.split(",").filter(Boolean);
  const setStatus = (id: string, s: "loaded" | "denied" | "error") =>
    useBoundStore.getState().setLinksFetchStatus(id, s);
  fetch(`${LINKS_ENDPOINT}?for_tables=${encodeURIComponent(tableIdsCsv)}`, {
    credentials: "include",
  })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const responseJson = await res.text();
      useBoundStore
        .getState()
        .clientApp?.on_link_list_received_for_tables(responseJson);
      // A table the server refused is absent from the response's `tables`, so
      // it stays uncached in WASM; mark it denied rather than loaded.
      const covered = new Set(
        (JSON.parse(responseJson) as { tables?: string[] }).tables ?? [],
      );
      tableIds.forEach((id) =>
        setStatus(id, covered.has(id) ? "loaded" : "denied"),
      );
      bumpLinksVersion();
    })
    .catch((err) => {
      tableIds.forEach((id) => setStatus(id, "error"));
      bumpLinksVersion();
      reportLinksError("fetch (tables)", err);
    });
}
